import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarRange, Plus, X } from 'lucide-react';
import { SupportedCurrency, Transaction } from '../types';
import { InstrumentSearchResult, ResolvedInstrument } from '../types/instruments';
import { resolveInstrument, searchInstruments } from '../services/instrumentService';

interface TransactionModalProps {
    open: boolean;
    defaultCurrency: SupportedCurrency;
    onClose: () => void;
    onSave: (transaction: Transaction, addMore?: boolean) => void;
    suggestions?: { symbol: string; name: string }[];
    initialDraft?: TransactionModalDraft | null;
}

export interface TransactionModalDraft {
    symbol?: string;
    name?: string;
    exchange?: string;
    quantity?: number | string;
    price?: number | string;
    priceCurrency?: string;
}

type TransactionMode = 'trade' | 'income';
type TransactionAction = Transaction['action'];
type ManualQuoteUnit = '' | 'GBX' | 'GBP' | 'USD' | 'EUR';
type TradePriceEnteredAs = 'GBP' | 'GBX';

interface FormState {
    mode: TransactionMode;
    symbol: string;
    name: string;
    action: TransactionAction;
    date: string;
    quantity: string;
    price: string;
    amount: string;
    currency: SupportedCurrency;
    priceCurrency: SupportedCurrency;
    tradePriceEnteredAs: TradePriceEnteredAs;
    fee: string;
    tax: string;
}

const createDefaultForm = (currency: SupportedCurrency): FormState => ({
    mode: 'trade',
    symbol: '',
    name: '',
    action: 'Buy',
    date: new Date().toISOString().split('T')[0],
    quantity: '',
    price: '',
    amount: '',
    currency,
    priceCurrency: currency,
    tradePriceEnteredAs: 'GBP',
    fee: '',
    tax: '',
});

const currencyOptions: SupportedCurrency[] = ['GBP', 'USD', 'EUR'];

const asSupportedCurrency = (value?: string): SupportedCurrency | null => {
    const normalized = (value || '').toUpperCase();
    if (normalized === 'GBP' || normalized === 'USD' || normalized === 'EUR') {
        return normalized;
    }
    return null;
};

const normalizeExchange = (value?: string): string => {
    if (!value) return '';
    return value
        .trim()
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .find(Boolean) || '';
};

const parseDraftNumber = (value?: number | string): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value !== 'string') return null;
    const normalized = value.trim().replace(/\s+/g, '').replace(',', '.');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatDraftNumber = (value: number, maxDecimals: number): string => {
    if (!Number.isFinite(value)) return '';
    const rounded = Number(value.toFixed(maxDecimals));
    return String(rounded);
};

const normalizeSymbol = (...values: Array<string | null | undefined>): string => {
    for (const value of values) {
        if (typeof value !== 'string') continue;
        const normalized = value.trim().toUpperCase();
        if (normalized) return normalized;
    }
    return '';
};

const formatPounds = (value: number): string => {
    if (!Number.isFinite(value)) return '£0.00';
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function TransactionModal({ open, onClose, onSave, defaultCurrency, initialDraft }: TransactionModalProps) {
    const [form, setForm] = useState<FormState>(() => createDefaultForm(defaultCurrency));
    const dateInputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<InstrumentSearchResult[]>([]);
    const [selectedInstrument, setSelectedInstrument] = useState<ResolvedInstrument | null>(null);
    const [selectedSearchResult, setSelectedSearchResult] = useState<InstrumentSearchResult | null>(null);
    const [manualQuoteUnit, setManualQuoteUnit] = useState<ManualQuoteUnit>('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const actionOptions: TransactionAction[] = useMemo(
        () => (form.mode === 'trade' ? ['Buy', 'Sell', 'Deposit', 'Withdrawal'] : ['Dividend']),
        [form.mode]
    );

    useEffect(() => {
        if (open) {
            const nextForm = createDefaultForm(defaultCurrency);
            const draftPriceCurrency = asSupportedCurrency(initialDraft?.priceCurrency);
            const draftQuantity = parseDraftNumber(initialDraft?.quantity);
            const draftPrice = parseDraftNumber(initialDraft?.price);
            setForm({
                ...nextForm,
                symbol: (initialDraft?.symbol || '').trim().toUpperCase(),
                name: initialDraft?.name || '',
                quantity: draftQuantity != null && draftQuantity > 0 ? formatDraftNumber(draftQuantity, 6) : '',
                price: draftPrice != null && draftPrice > 0 ? formatDraftNumber(draftPrice, 4) : '',
                priceCurrency: draftPriceCurrency || nextForm.priceCurrency,
                currency: draftPriceCurrency || nextForm.currency,
                tradePriceEnteredAs: 'GBP',
            });
            setQuery((initialDraft?.symbol || '').trim().toUpperCase());
            setResults([]);
            setSelectedInstrument(null);
            setSelectedSearchResult(null);
            setManualQuoteUnit('');
            setLoading(false);
            setError(null);
            setShowSuggestions(false);
        }
    }, [open, defaultCurrency, initialDraft]);

    useEffect(() => {
        if (!open || selectedInstrument || selectedSearchResult) return;
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setError(null);
            return;
        }

        let active = true;
        const timeoutId = window.setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const next = await searchInstruments(q, 10);
                if (!active) return;
                setResults(next);
                setShowSuggestions(true);
            } catch (e) {
                if (!active) return;
                setResults([]);
                setError(e instanceof Error ? e.message : 'Failed to search instruments.');
            } finally {
                if (active) setLoading(false);
            }
        }, 400);

        return () => {
            active = false;
            window.clearTimeout(timeoutId);
        };
    }, [open, query, selectedInstrument, selectedSearchResult]);


    const getProviderSymbol = (instrument: ResolvedInstrument | null, searchResult: InstrumentSearchResult | null): string => (
        instrument?.providers?.eodhd?.code || searchResult?.providerSymbol || ''
    ).trim().toUpperCase();

    const requiresManualQuoteUnit = (instrument: ResolvedInstrument | null, searchResult: InstrumentSearchResult | null): boolean => {
        const providerSymbol = getProviderSymbol(instrument, searchResult);
        const exchange = normalizeExchange(instrument?.exchange || searchResult?.exchange);
        return providerSymbol.endsWith('.LSE') || exchange === 'LSE';
    };

    const isUsMarketInstrument = (instrument: ResolvedInstrument | null, searchResult: InstrumentSearchResult | null): boolean => {
        const providerSymbol = getProviderSymbol(instrument, searchResult);
        const exchange = normalizeExchange(instrument?.exchange || searchResult?.exchange);
        return providerSymbol.endsWith('.US') || exchange === 'US' || exchange === 'NASDAQ' || exchange === 'NYSE' || exchange === 'AMEX';
    };

    const resolveProviderQuoteUnit = (instrument: ResolvedInstrument | null, searchResult: InstrumentSearchResult | null): ManualQuoteUnit => {
        const existingQuoteUnit = asSupportedCurrency(instrument?.quoteUnit || instrument?.manualQuoteUnit);
        if (existingQuoteUnit) return existingQuoteUnit;
        if (isUsMarketInstrument(instrument, searchResult)) return 'USD';
        const providerCurrency = asSupportedCurrency(instrument?.currency || searchResult?.currency);
        return providerCurrency || '';
    };

    const getResolveQuoteUnitContext = (instrument: ResolvedInstrument | null, searchResult: InstrumentSearchResult | null) => {
        const isLse = requiresManualQuoteUnit(instrument, searchResult);
        const quoteUnitToSend = isLse ? manualQuoteUnit : resolveProviderQuoteUnit(instrument, searchResult);
        return { isLse, quoteUnitToSend };
    };

    const confirmLseMajorQuoteUnitIfNeeded = (quoteUnitContext: ReturnType<typeof getResolveQuoteUnitContext>): boolean => {
        if (!quoteUnitContext.isLse || quoteUnitContext.quoteUnitToSend !== 'GBP') {
            return true;
        }
        return window.confirm('Most LSE shares are quoted in GBX/pence. GBP/pounds is usually only correct for some ETFs/funds. Are you sure this instrument’s live market quote is in pounds?');
    };

    const logResolveRequest = (searchResult: InstrumentSearchResult, quoteUnitContext: ReturnType<typeof getResolveQuoteUnitContext>, symbol: string) => {
        console.log('[manual-instrument] resolve-request', {
            symbol,
            exchange: searchResult.exchange,
            providerSymbol: searchResult.providerSymbol,
            costCurrency: form.priceCurrency,
            formPriceCurrency: form.priceCurrency,
            manualQuoteUnit,
            quoteUnitToSend: quoteUnitContext.quoteUnitToSend,
            isLse: quoteUnitContext.isLse,
        });
    };

    const manualQuoteUnitOptions = requiresManualQuoteUnit(selectedInstrument, selectedSearchResult)
        ? [
              { value: 'GBX' as const, label: 'GBX / pence — most UK shares' },
              { value: 'GBP' as const, label: 'GBP / pounds — only if quoted in pounds, often ETFs/funds' },
          ]
        : currencyOptions.map((value) => ({ value, label: value }));

    const selectedMarketQuoteUnit = (
        manualQuoteUnit ||
        selectedInstrument?.quoteUnit ||
        selectedInstrument?.manualQuoteUnit ||
        ''
    ).toUpperCase();
    const isLseGbxMarketQuote = requiresManualQuoteUnit(selectedInstrument, selectedSearchResult) && selectedMarketQuoteUnit === 'GBX';
    const enteredTradePrice = Number.parseFloat(form.price);
    const suggestedTradePrice = Number.isFinite(enteredTradePrice) ? enteredTradePrice / 100 : 0;
    const formatTradePriceInput = (value: number): string => {
        if (!Number.isFinite(value)) return '0';
        return value.toLocaleString('en-GB', { maximumFractionDigits: 6 });
    };
    const showPencePriceWarning =
        form.mode === 'trade' &&
        isLseGbxMarketQuote &&
        form.priceCurrency === 'GBP' &&
        form.tradePriceEnteredAs === 'GBP' &&
        Number.isFinite(enteredTradePrice) &&
        enteredTradePrice > 100;
    const pencePriceWarning = showPencePriceWarning
        ? `This looks like a pence price entered as pounds. Did you mean ${formatTradePriceInput(enteredTradePrice)} GBX = ${formatPounds(suggestedTradePrice)}?`
        : null;

    const handleManualQuoteUnitChange = (value: ManualQuoteUnit) => {
        setManualQuoteUnit(value);
        if (value === 'GBX') {
            setForm((prev) => ({
                ...prev,
                priceCurrency: 'GBP',
                tradePriceEnteredAs: 'GBX',
            }));
        }
    };

    const handleInputChange = (key: keyof FormState, value: string | number) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const openDatePicker = () => {
        const input = dateInputRef.current;
        if (input?.showPicker) {
            input.showPicker();
        } else {
            input?.focus();
            input?.click();
        }
    };

    const handleSelectResult = async (item: InstrumentSearchResult) => {
        const selectedTicker = normalizeSymbol(item.ticker);
        if (!selectedTicker) {
            setError('Selected instrument is missing a ticker symbol. Please choose another result.');
            return;
        }
        setLoading(true);
        setError(null);
        setShowSuggestions(false);
        setResults([]);
        setQuery(selectedTicker);
        setSelectedSearchResult(item);
        setManualQuoteUnit(requiresManualQuoteUnit(null, item) ? '' : resolveProviderQuoteUnit(null, item));
        if (requiresManualQuoteUnit(null, item)) {
            setForm((prev) => ({
                ...prev,
                symbol: selectedTicker,
                name: prev.name?.trim() ? prev.name : item.name,
            }));
            setLoading(false);
            return;
        }
        try {
            const quoteUnitContext = getResolveQuoteUnitContext(null, item);
            if (quoteUnitContext.isLse && !quoteUnitContext.quoteUnitToSend) {
                alert('Please choose whether this LSE instrument is quoted in GBX / pence or GBP / pounds before saving.');
                return;
            }
            if (!confirmLseMajorQuoteUnitIfNeeded(quoteUnitContext)) {
                return;
            }
            logResolveRequest(item, quoteUnitContext, selectedTicker);
            const resolved = await resolveInstrument({
                source: item.source,
                providerSymbol: item.providerSymbol,
                symbol: selectedTicker,
                exchange: item.exchange,
                currency: form.priceCurrency,
                costCurrency: form.priceCurrency,
                manualQuoteUnit: quoteUnitContext.quoteUnitToSend,
                ...(quoteUnitContext.isLse && quoteUnitContext.quoteUnitToSend === 'GBP' ? { lseMajorUnitConfirmed: true } : {}),
            });
            const resolvedSymbol = normalizeSymbol(resolved.ticker, selectedTicker);
            setSelectedInstrument(resolved);
            setSelectedSearchResult(null);
            setQuery(resolvedSymbol);
            setShowSuggestions(false);
            setForm((prev) => {
                return {
                    ...prev,
                    symbol: resolvedSymbol,
                    name: prev.name?.trim() ? prev.name : resolved.name || item.name,
                };
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to resolve selected instrument.');
        } finally {
            setLoading(false);
        }
    };

    const resolveInstrumentFromDraft = async (): Promise<ResolvedInstrument | null> => {
        const targetSymbol = (query || form.symbol).trim().toUpperCase();
        if (!targetSymbol) return null;

        try {
            setLoading(true);
            setError(null);
            const searchResults = await searchInstruments(targetSymbol, 10);
            const targetExchange = normalizeExchange(initialDraft?.exchange);
            const exactMatches = searchResults.filter((item) => {
                const sameTicker = normalizeSymbol(item.ticker) === targetSymbol;
                const sameExchange = !targetExchange || normalizeExchange(item.exchange) === targetExchange;
                return sameTicker && sameExchange;
            });

            if (exactMatches.length !== 1) {
                setResults(searchResults);
                setShowSuggestions(true);
                return null;
            }

            const exact = exactMatches[0];
            const quoteUnitContext = getResolveQuoteUnitContext(null, exact);
            if (quoteUnitContext.isLse && !quoteUnitContext.quoteUnitToSend) {
                setSelectedSearchResult(exact);
                setQuery(normalizeSymbol(exact.ticker, targetSymbol));
                setShowSuggestions(false);
                setForm((prev) => ({
                    ...prev,
                    symbol: normalizeSymbol(exact.ticker, targetSymbol),
                    name: prev.name?.trim() ? prev.name : exact.name,
                }));
                alert('Please choose whether this LSE instrument is quoted in GBX / pence or GBP / pounds before saving.');
                return null;
            }
            if (!confirmLseMajorQuoteUnitIfNeeded(quoteUnitContext)) {
                return null;
            }
            logResolveRequest(exact, quoteUnitContext, targetSymbol);
            const resolved = await resolveInstrument({
                source: exact.source,
                providerSymbol: exact.providerSymbol,
                symbol: targetSymbol,
                exchange: exact.exchange || initialDraft?.exchange,
                currency: form.priceCurrency,
                costCurrency: form.priceCurrency,
                manualQuoteUnit: quoteUnitContext.quoteUnitToSend,
                ...(quoteUnitContext.isLse && quoteUnitContext.quoteUnitToSend === 'GBP' ? { lseMajorUnitConfirmed: true } : {}),
            });
            const resolvedSymbol = normalizeSymbol(resolved.ticker, exact.ticker, targetSymbol);

            setSelectedInstrument(resolved);
            setSelectedSearchResult(null);
            setQuery(resolvedSymbol);
            setShowSuggestions(false);
            setForm((prev) => ({
                ...prev,
                symbol: resolvedSymbol,
                name: prev.name?.trim() ? prev.name : resolved.name || exact.name,
            }));

            return resolved;
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to resolve instrument.');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (addMore = false) => {
        let instrument = selectedInstrument;
        if (!instrument && selectedSearchResult) {
            const quoteUnitContext = getResolveQuoteUnitContext(null, selectedSearchResult);
            if (quoteUnitContext.isLse && !quoteUnitContext.quoteUnitToSend) {
                alert('Please choose whether this LSE instrument is quoted in GBX / pence or GBP / pounds before saving.');
                return;
            }
            if (!confirmLseMajorQuoteUnitIfNeeded(quoteUnitContext)) {
                return;
            }
            const symbolToResolve = normalizeSymbol(selectedSearchResult.ticker, form.symbol, query);
            logResolveRequest(selectedSearchResult, quoteUnitContext, symbolToResolve);
            instrument = await resolveInstrument({
                source: selectedSearchResult.source,
                providerSymbol: selectedSearchResult.providerSymbol,
                symbol: symbolToResolve,
                exchange: selectedSearchResult.exchange,
                currency: form.priceCurrency,
                costCurrency: form.priceCurrency,
                manualQuoteUnit: quoteUnitContext.quoteUnitToSend,
                ...(quoteUnitContext.isLse && quoteUnitContext.quoteUnitToSend === 'GBP' ? { lseMajorUnitConfirmed: true } : {}),
            });
            setSelectedInstrument(instrument);
        }
        if (!instrument) {
            instrument = await resolveInstrumentFromDraft();
            if (!instrument) {
                alert('Please select the exact instrument from search results before saving.');
                return;
            }
        }
        if (requiresManualQuoteUnit(instrument, selectedSearchResult) && !manualQuoteUnit) {
            alert('Please choose whether this LSE instrument is quoted in GBX / pence or GBP / pounds before saving.');
            return;
        }
        if (!form.date) {
            alert('Please pick a date for the transaction.');
            return;
        }

        const quantityValue = parseFloat(form.quantity) || 0;
        const priceValue = parseFloat(form.price) || 0;
        const amountValue = parseFloat(form.amount) || 0;
        const fee = Math.max(parseFloat(form.fee) || 0, 0);
        const tax = Math.max(parseFloat(form.tax) || 0, 0);

        const quantity = form.mode === 'income' ? quantityValue || 0 : Math.max(quantityValue, 0);
        const transactionMarketQuoteUnit = (
            manualQuoteUnit ||
            instrument.quoteUnit ||
            instrument.manualQuoteUnit ||
            resolveProviderQuoteUnit(instrument, selectedSearchResult) ||
            form.priceCurrency
        ).toUpperCase();
        const shouldNormalizeGbxTradeInput =
            form.mode === 'trade' &&
            requiresManualQuoteUnit(instrument, selectedSearchResult) &&
            transactionMarketQuoteUnit === 'GBX' &&
            form.tradePriceEnteredAs === 'GBX';
        const normalizedTradePrice = shouldNormalizeGbxTradeInput ? priceValue * 0.01 : priceValue;
        const tradeAmount = form.mode === 'trade' ? Math.max(0, quantity * normalizedTradePrice + fee + tax) : 0;
        const incomeAmount = form.mode === 'income' ? Math.max(0, amountValue - fee - tax) : 0;
        const finalAmount = form.mode === 'trade' ? tradeAmount : incomeAmount;

        const action: TransactionAction = form.mode === 'income' ? 'Dividend' : form.action;
        const finalPrice =
            form.mode === 'income' && priceValue === 0 && quantity > 0 && incomeAmount > 0 ? incomeAmount / quantity : normalizedTradePrice;

        const transactionCurrency = form.mode === 'trade' ? form.priceCurrency : form.currency;
        const finalSymbol = normalizeSymbol(instrument.ticker, form.symbol, query);
        if (!finalSymbol) {
            alert('Unable to determine instrument symbol. Please reselect the instrument and try again.');
            return;
        }
        const transaction: Transaction = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            date: form.date,
            action,
            symbol: finalSymbol,
            displaySymbol: finalSymbol,
            name: form.name || instrument.name || finalSymbol,
            instrumentId: instrument.instrumentId,
            exchange: instrument.exchange || undefined,
            quantity,
            price: finalPrice,
            priceCurrency: form.priceCurrency,
            amount: finalAmount,
            fees: fee,
            tax,
            currency: transactionCurrency,
            costCurrency: form.mode === 'trade' ? transactionCurrency : undefined,
            marketQuoteUnit: form.mode === 'trade' ? transactionMarketQuoteUnit || undefined : undefined,
            originalInputPrice: form.mode === 'trade' ? priceValue : undefined,
            originalInputPriceUnit: form.mode === 'trade'
                ? (transactionMarketQuoteUnit === 'GBX' ? form.tradePriceEnteredAs : form.priceCurrency)
                : undefined,
            source: 'manual',
            sourceLabel: 'Added manually',
        };

        onSave(transaction, addMore);
        if (!addMore) {
            onClose();
            return;
        }

        setForm((prev) => ({ ...createDefaultForm(defaultCurrency), mode: prev.mode }));
        setQuery('');
        setResults([]);
        setSelectedInstrument(null);
        setSelectedSearchResult(null);
        setManualQuoteUnit('');
        setError(null);
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-[#111118] border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl mt-10">
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-indigo-400">New transaction</p>
                        <h3 className="text-lg font-semibold text-white">Add Trade or Dividend</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                        aria-label="Close modal"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 pt-4">
                    <div className="inline-flex bg-white/5 p-1 rounded-xl border border-white/10 mb-4 text-sm">
                        <button
                            type="button"
                            onClick={() => setForm((prev) => ({ ...prev, mode: 'trade', action: 'Buy' }))}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                form.mode === 'trade' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-300 hover:text-white'
                            }`}
                        >
                            Trades
                        </button>
                        <button
                            type="button"
                            onClick={() => setForm((prev) => ({ ...prev, mode: 'income', action: 'Dividend' }))}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                form.mode === 'income' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-300 hover:text-white'
                            }`}
                        >
                            Incomes
                        </button>
                    </div>
                </div>

                <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col space-y-2 relative md:col-span-2">
                            <label className="text-sm text-slate-300">
                                Instrument search <span className="text-indigo-400">*</span>
                            </label>

                            {!selectedInstrument && !selectedSearchResult ? (
                                <>
                                    <input
                                        value={query}
                                        onChange={(e) => {
                                            setQuery(e.target.value);
                                            setShowSuggestions(true);
                                        }}
                                        onFocus={() => setShowSuggestions(true)}
                                        onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
                                        placeholder="Search by ticker, company, or ISIN"
                                        className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                                    />
                                    {loading && <p className="text-xs text-slate-400">Searching instruments…</p>}
                                    {error && <p className="text-xs text-rose-400">{error}</p>}
                                    {showSuggestions && results.length > 0 && (
                                        <div className="absolute z-20 top-[74px] w-full rounded-xl border border-white/10 bg-[#111118] shadow-xl max-h-64 overflow-auto">
                                            {results.map((item) => (
                                                <button
                                                    key={`${item.source}:${item.providerSymbol}`}
                                                    type="button"
                                                    className="w-full text-left px-4 py-3 text-sm text-slate-200 hover:bg-white/5 transition-colors"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => void handleSelectResult(item)}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="font-semibold">{item.ticker} — {item.name}</div>
                                                        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full border border-white/20 text-slate-300">{item.source}</span>
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-1">{item.exchange} — {item.currency}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="rounded-lg border border-indigo-400/40 bg-indigo-500/10 px-3 py-3 flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-semibold text-indigo-100">
                                            {(selectedInstrument?.ticker || selectedSearchResult?.ticker)} — {(selectedInstrument?.name || selectedSearchResult?.name)}
                                        </div>
                                        <div className="text-xs text-indigo-200/80">
                                            {(selectedInstrument?.exchange || selectedSearchResult?.exchange)} — {(selectedInstrument?.currency || selectedSearchResult?.currency)}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedInstrument(null);
                                            setSelectedSearchResult(null);
                                            setManualQuoteUnit('');
                                            setQuery('');
                                            setResults([]);
                                            setError(null);
                                        }}
                                        className="text-xs text-indigo-200 hover:text-white underline"
                                    >
                                        Change
                                    </button>
                                </div>
                            )}
                        </div>

                        {(selectedInstrument || selectedSearchResult) && (
                            <div className="flex flex-col space-y-2">
                                <label className="text-sm text-slate-300">
                                    Market quote unit {requiresManualQuoteUnit(selectedInstrument, selectedSearchResult) && <span className="text-indigo-400">*</span>}
                                </label>
                                <select
                                    value={manualQuoteUnit}
                                    onChange={(e) => handleManualQuoteUnitChange(e.target.value as ManualQuoteUnit)}
                                    className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                >
                                    <option value="">Select quote unit</option>
                                    {manualQuoteUnitOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-400">
                                    {requiresManualQuoteUnit(selectedInstrument, selectedSearchResult)
                                        ? 'Choose GBX / pence for most UK shares such as BARC, LGEN, HSBA. Choose GBP / pounds only for instruments genuinely quoted in pounds, often some ETFs/funds.'
                                        : 'This is the market quote unit used by live prices, separate from your holding/cost currency.'}
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col space-y-2">
                            <label className="text-sm text-slate-300">Friendly name</label>
                            <input
                                value={form.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Optional display name"
                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                            />
                        </div>

                        <div className="flex flex-col space-y-2">
                            <label className="text-sm text-slate-300">Date</label>
                            <div className="relative">
                                <input
                                    ref={dateInputRef}
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => handleInputChange('date', e.target.value)}
                                    className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={openDatePicker}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                >
                                    <CalendarRange size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col space-y-2">
                            <label className="text-sm text-slate-300">Action</label>
                            <select
                                value={form.action}
                                onChange={(e) => handleInputChange('action', e.target.value as TransactionAction)}
                                disabled={form.mode === 'income'}
                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-indigo-500 outline-none disabled:opacity-60"
                            >
                                {actionOptions.map((action) => (
                                    <option key={action} value={action}>
                                        {action}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col space-y-2">
                            <label className="text-sm text-slate-300">Fees</label>
                            <input
                                type="number"
                                min="0"
                                value={form.fee}
                                onChange={(e) => handleInputChange('fee', e.target.value)}
                                placeholder="0"
                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                            />
                        </div>

                        <div className="flex flex-col space-y-2">
                            <label className="text-sm text-slate-300">Tax</label>
                            <input
                                type="number"
                                min="0"
                                value={form.tax}
                                onChange={(e) => handleInputChange('tax', e.target.value)}
                                placeholder="0"
                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                            />
                        </div>

                        {form.mode === 'trade' && (
                            <>
                                <div className="flex flex-col space-y-2">
                                    <label className="text-sm text-slate-300">Shares</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={form.quantity}
                                        onChange={(e) => handleInputChange('quantity', e.target.value)}
                                        placeholder="Number of shares"
                                        className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                                    />
                                </div>
                                <div className="flex flex-col space-y-2">
                                    <div className="flex space-x-2">
                                        <div className="flex-1 flex flex-col space-y-2">
                                            <label className="text-sm text-slate-300">Trade price / cost per share</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="any"
                                                value={form.price}
                                                onChange={(e) => handleInputChange('price', e.target.value)}
                                                placeholder="Price per share"
                                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                                            />
                                        </div>
                                        <div className="w-32 flex flex-col space-y-2">
                                            <label className="text-sm text-slate-300">Cost currency</label>
                                            <select
                                                aria-label="Cost currency"
                                                value={form.priceCurrency}
                                                onChange={(e) => handleInputChange('priceCurrency', e.target.value as SupportedCurrency)}
                                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-3 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                            >
                                                {currencyOptions.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {isLseGbxMarketQuote && (
                                        <div className="flex flex-col space-y-2">
                                            <label className="text-sm text-slate-300">Trade price entered as</label>
                                            <select
                                                aria-label="Trade price entered as"
                                                value={form.tradePriceEnteredAs}
                                                onChange={(e) => handleInputChange('tradePriceEnteredAs', e.target.value as TradePriceEnteredAs)}
                                                className="w-full bg-[#1b1b23] border border-white/10 rounded-lg px-3 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                            >
                                                <option value="GBP">GBP / pounds</option>
                                                <option value="GBX">GBX / pence</option>
                                            </select>
                                        </div>
                                    )}
                                    <p className="text-xs text-slate-400">
                                        Enter the actual trade price per share. For LSE instruments quoted in GBX/pence, choose whether your typed price is already in GBP/pounds or is a GBX/pence input that should be converted to GBP cost basis.
                                    </p>
                                    {pencePriceWarning && (
                                        <p className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                                            {pencePriceWarning}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}

                        {form.mode === 'income' && (
                            <>
                                <div className="flex flex-col space-y-2">
                                    <label className="text-sm text-slate-300">Total received</label>
                                    <div className="flex space-x-2">
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={form.amount}
                                            onChange={(e) => handleInputChange('amount', e.target.value)}
                                            placeholder="Total dividend"
                                            className="flex-1 bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                                        />
                                        <select
                                            value={form.currency}
                                            onChange={(e) => handleInputChange('currency', e.target.value as SupportedCurrency)}
                                            className="w-28 bg-[#1b1b23] border border-white/10 rounded-lg px-3 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                        >
                                            {currencyOptions.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-col space-y-2">
                                    <label className="text-sm text-slate-300">Per share (optional)</label>
                                    <div className="flex space-x-2">
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={form.price}
                                            onChange={(e) => handleInputChange('price', e.target.value)}
                                            placeholder="Dividend per share"
                                            className="flex-1 bg-[#1b1b23] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                                        />
                                        <select
                                            value={form.priceCurrency}
                                            onChange={(e) => handleInputChange('priceCurrency', e.target.value as SupportedCurrency)}
                                            className="w-28 bg-[#1b1b23] border border-white/10 rounded-lg px-3 py-3 text-sm text-white focus:border-indigo-500 outline-none"
                                        >
                                            {currencyOptions.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleSubmit(true)}
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-indigo-500/40 text-indigo-100 hover:bg-indigo-500/10 transition-colors"
                        >
                            Save & add more
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleSubmit(false)}
                            className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors shadow-lg shadow-indigo-500/20 inline-flex items-center"
                        >
                            <Plus size={16} className="mr-2" /> Save transaction
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
